import React, { useCallback, useEffect, useImperativeHandle, useState, forwardRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  UserCheck, UserX, UserPlus, MessageSquare, FileText,
  RefreshCw, Sparkles, AlertCircle, Clock,
} from 'lucide-react';
import { adminApi } from '@/lib/api';

const ICON_MAP = {
  REPORT_CREATED: FileText,
  REPORT_ASSIGNED: UserPlus,
  REPORT_REASSIGNED: RefreshCw,
  REPORT_UNASSIGNED: UserX,
  REPORT_BULK_ASSIGNED: UserPlus,
  STATUS_CHANGED: AlertCircle,
  MESSAGE_SENT: MessageSquare,
  AI_ANALYSIS: Sparkles,
  AI_SUGGESTIONS_APPROVED: Sparkles,
  REPORT_VIEWED: UserCheck,
};

const formatDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-PT', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
};

const renderEventBody = (ev) => {
  if (ev.kind === 'message') {
    return (
      <p className="text-sm text-slate-600 italic line-clamp-3">
        {ev.details?.message || '(sem conteúdo)'}
      </p>
    );
  }
  if (ev.action === 'REPORT_REASSIGNED') {
    return (
      <div className="text-sm text-slate-600 space-y-1">
        <p>
          {ev.subject?.from?.name || 'Anterior'} → <span className="font-medium text-slate-900">{ev.subject?.to?.name || 'Novo gestor'}</span>
        </p>
        {ev.subject?.reason && (
          <blockquote className="border-l-2 border-slate-300 pl-2 text-slate-500 italic">
            "{ev.subject.reason}"
          </blockquote>
        )}
      </div>
    );
  }
  if (ev.action === 'REPORT_ASSIGNED') {
    return (
      <p className="text-sm text-slate-600">
        Atribuída a <span className="font-medium text-slate-900">{ev.subject?.to?.name || 'gestor'}</span>
      </p>
    );
  }
  if (ev.action === 'REPORT_UNASSIGNED') {
    return <p className="text-sm text-slate-600">Atribuição removida</p>;
  }
  if (ev.action === 'STATUS_CHANGED' && ev.details?.status) {
    return <p className="text-sm text-slate-600">Novo estado: <span className="font-medium">{ev.details.status}</span></p>;
  }
  return null;
};

const CaseTimelineCard = forwardRef(({ reportId }, ref) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchTimeline = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    try {
      const res = await adminApi.getReportTimeline(reportId);
      setEvents(res.data?.events || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar cronologia');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);
  useImperativeHandle(ref, () => fetchTimeline, [fetchTimeline]);

  return (
    <Card data-testid="case-timeline-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-4 h-4" /> Cronologia do Caso
        </CardTitle>
        <button
          type="button"
          onClick={fetchTimeline}
          className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="case-timeline-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </CardHeader>
      <CardContent>
        {loading && <p className="text-sm text-slate-500">A carregar…</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!loading && !error && events.length === 0 && (
          <p className="text-sm text-slate-500">Ainda sem eventos registados.</p>
        )}
        {!loading && !error && events.length > 0 && (
          <ol className="relative border-l border-slate-200 space-y-4 ml-3">
            {events.map((ev, i) => {
              const Icon = ICON_MAP[ev.action] || FileText;
              return (
                <li key={`${ev.created_at}-${i}`} className="ml-4">
                  <span className="absolute -left-[9px] mt-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-white border border-slate-300 text-slate-600">
                    <Icon className="w-2.5 h-2.5" />
                  </span>
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <p className="text-sm font-medium text-slate-900">{ev.title}</p>
                    <p className="text-xs text-slate-500">{formatDateTime(ev.created_at)}</p>
                  </div>
                  {ev.actor?.name && (
                    <p className="text-xs text-slate-500">por {ev.actor.name}</p>
                  )}
                  <div className="mt-1">{renderEventBody(ev)}</div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
});

CaseTimelineCard.displayName = 'CaseTimelineCard';
export default CaseTimelineCard;
